import { getApiBaseUrl } from '../../config/runtimeConfig';
import { sessionFetch } from '../../services/session';


function httpError(message, status) {
  const error = new Error(message);
  error.status = status;
  return error;
}

async function request(path, options = {}) {
  let response;
  try {
    response = await sessionFetch(`${getApiBaseUrl()}${path}`, {
      credentials: 'include',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
      signal: AbortSignal.timeout(20000),
    });
  } catch (error) {
    throw httpError(error.name === 'TimeoutError'
      ? 'The request timed out. Please try again.'
      : 'Unable to connect. Please check your connection and try again.');
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw httpError(
      typeof data?.message === 'string' ? data.message : 'Unable to complete the customer request.',
      response.status,
    );
  }
  return data;
}

export function isUnpersistedCustomerError(error) {
  return !error?.status || error.status === 404;
}

function toCustomer(row) {
  return {
    id: row.id,
    name: row.name || '',
    mobile_number: row.mobile_number || '',
    address: row.address || '',
    logo: row.logo || '',
    is_deleted: false,
  };
}

const customersInFlight = new Map();
export function listCustomers(filters = {}) {
  const params = new URLSearchParams();
  if (filters.warehouse_id) params.set('warehouse_id', String(filters.warehouse_id));
  const path = `/customers${params.toString() ? `?${params}` : ''}`;
  const key = `${getApiBaseUrl()}${path}`;
  if (!customersInFlight.has(key)) {
    customersInFlight.set(key, loadCustomers(path).finally(() => customersInFlight.delete(key)));
  }
  return customersInFlight.get(key);
}

async function loadCustomers(path) {
  try {
    const data = await request(path);
    return (data.customers || []).map(row => toCustomer(row));
  } catch (error) {
    if (isUnpersistedCustomerError(error)) return [];
    throw error;
  }
}

export async function getCustomer(id) {
  try {
    const data = await request(`/customers/${id}`);
    return data.customer ? toCustomer(data.customer) : null;
  } catch (error) {
    if (isUnpersistedCustomerError(error)) return null;
    throw error;
  }
}

const detailsInFlight = new Map();
export function getCustomerDetails(id) {
  const path = `/customers/${encodeURIComponent(id)}/details`;
  const key = `${getApiBaseUrl()}${path}`;
  if (!detailsInFlight.has(key)) detailsInFlight.set(key, request(path).finally(() => detailsInFlight.delete(key)));
  return detailsInFlight.get(key);
}

export async function createCustomer(payload) {
  const data = await request('/customers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: payload.name,
      mobile_number: payload.mobile_number,
      address: payload.address,
      logo: payload.logo,
    }),
  });
  const customer = data.customer;
  if (!customer?.id) throw httpError('Customer was not saved to the database.');
  return toCustomer(customer);
}

export async function updateCustomer(id, payload) {
  const data = await request(`/customers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: payload.name,
      mobile_number: payload.mobile_number,
      address: payload.address,
      logo: payload.logo,
    }),
  });
  const customer = data.customer;
  if (!customer?.id) throw httpError('Customer was not saved to the database.');
  return toCustomer(customer);
}

export async function deleteCustomer(id) {
  await request(`/customers/${id}`, { method: 'DELETE' });
}
